import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProductsByBrand } from '../../Redux/slice/productSlice';
import { fetchBrands } from '../../Redux/slice/brandSlice';
import { Helmet } from 'react-helmet';
import { Empty,} from 'antd';
import RelatedBrands from './RelatedBrands';

const Brand = () => {
  const { brandId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { brandProducts, loading } = useSelector((state) => state.products);
  const { brands } = useSelector((state) => state.brands);

  const [minPrice] = useState(0);
  const [maxPrice] = useState(200000);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [imageLoadStates, setImageLoadStates] = useState({});

  const observer = useRef();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    dispatch(fetchBrands());
    dispatch(fetchProductsByBrand(brandId));
  }, [dispatch, brandId]);

  const handleImageLoad = (productId) => {
    setImageLoadStates((prev) => ({ ...prev, [productId]: true }));
  };

  const handleImageError = (productId) => {
    setImageLoadStates((prev) => ({ ...prev, [productId]: 'error' }));
  };

  const selectedBrand = brands.find((brand) => brand.brandId === brandId);
  const filteredBrands = selectedBrand
    ? brands.filter((brand) => brand.categoryId === selectedBrand.categoryId)
    : [];

    const filteredProducts = (brandProducts || [])
    .filter((product) => product.price >= minPrice && product.price <= maxPrice)
    .sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));

  const lastProductRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          setCurrentPage((prevPage) => prevPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading]
  );

  const currentProducts = filteredProducts.slice(0, currentPage * itemsPerPage);

  const formatPrice = (price) => price.toLocaleString();
  const jsonLdData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": selectedBrand ? `${selectedBrand.brandName} Products` : "Brand Products",

    "itemListElement": currentProducts.map((product, index) => ({
      "@type": "Product",
      "position": index + 1,
      "name": product.productName,
      "image": `https://smfteapi.salesmate.app/Media/Products_Images/${product.productImage.split('\\').pop()}`,
      "description": product.description,
      "brand": {
        "@type": "Brand",
        "name": selectedBrand ? selectedBrand.brandName : "Unknown"
      },
      "offers": {
        "@type": "Offer",
        "priceCurrency": "GHS",
        "price": product.price,
        "availability": "https://schema.org/InStock",
        "url": `${window.location.origin}/product/${product.productID}`
      },
      "review": {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "Verified Buyer",
        },
        "reviewBody": "Great deal on this product! Highly recommended.",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5",
        },
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.7",
        "reviewCount": "234",
      },
    }))
  };

  return (
    <div className="container mx-auto p-4 md:p-6">
     <Helmet>
        <title>{selectedBrand ? `${selectedBrand.brandName} Products` : "Brand Products"}</title>
        <meta
          name="description"
          content={
            selectedBrand
              ? `Explore a variety of products from ${selectedBrand.brandName}.`
              : "Discover a wide range of branded products."
          }
        />
        <meta name="robots" content="index, follow" />

        {/* Open Graph Meta Tags */}
        <meta property="og:title" content={selectedBrand ? `${selectedBrand.brandName} Products` : "Brand Products"} />
        <meta property="og:description" content={`Find top-quality products from ${selectedBrand ? selectedBrand.brandName : 'various brands'}.`} />
        <meta property="og:image" content={`${window.location.origin}/assets/frankoIcon.png`} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />

        {/* Twitter Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={selectedBrand ? `${selectedBrand.brandName} Products` : "Brand Products"} />
        <meta name="twitter:description" content={`Find top-quality products from ${selectedBrand ? selectedBrand.brandName : 'various brands'}.`} />
        <meta name="twitter:image" content={`${window.location.origin}/assets/frankoIcon.png`} />

        {/* Canonical URL */}
        <link rel="canonical" content={window.location.href} />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">{JSON.stringify(jsonLdData)}</script>
      </Helmet>

      <h3 className="text-md font-semibold mb-4 text-red-500">
        {selectedBrand ? `${selectedBrand.brandName} Products` : 'Brand Products'}
      </h3>

      <RelatedBrands
        filteredBrands={filteredBrands}
        selectedBrandId={brandId}
        navigate={navigate}
      />

   

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 12 }).map((_, index) => (
            <div
              key={index}
              className="animate-pulse border rounded-lg shadow p-3 bg-gray-100 relative"
            >
              <div
                className="absolute inset-0 bg-center bg-no-repeat opacity-10"
                style={{
                  backgroundImage: "url('/frankoIcon.png')",
                  backgroundSize: '90px',
                }}
              ></div>
              <div className="h-36 bg-gray-200 rounded-lg"></div>
              <div className="h-3 bg-gray-200 rounded w-3/4 mt-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2 mt-1"></div>
            </div>
          ))}
        </div>
      ) : currentProducts.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {currentProducts.map((product, index) => (
            <div
              ref={index === currentProducts.length - 1 ? lastProductRef : null}
              key={product.productID || index}
              className="p-4 bg-white border border-gray-200 rounded-lg shadow-md hover:shadow-xl transform transition-transform duration-300 hover:scale-105 cursor-pointer"
              onClick={() => navigate(`/product/${product.productID}`)}
            >
              <div className="h-32 md:h-32 lg:h-48 flex items-center justify-center mb-3">
                {!product.productImage || imageLoadStates[product.productID] === 'error' ? (
                  <div className="animate-pulse w-32 h-32 md:w-24 md:h-24 lg:w-48 lg:h-48 bg-gray-200 rounded-lg"></div>
                ) : (
                  <>
                    {!imageLoadStates[product.productID] && (
                      <div className="animate-pulse w-32 h-32 md:w-24 md:h-24 lg:w-48 lg:h-48 bg-gray-200 rounded-lg"></div>
                    )}
                    <img
                      src={`https://smfteapi.salesmate.app/Media/Products_Images/${product.productImage.split('\\').pop()}`}
                      alt={product.productName}
                      className={`w-32 md:w-24 lg:w-48 object-cover rounded-lg ${
                        !imageLoadStates[product.productID] ? 'hidden' : ''
                      }`}
                      onLoad={() => handleImageLoad(product.productID)}
                      onError={() => handleImageError(product.productID)}
                    />
                  </>
                )}
              </div>
              <div className="flex flex-col space-y-1">
                <h2 className="text-sm md:text-md font-semibold text-gray-800 line-clamp-2">
                  {product.productName}
                </h2>
                <span className="text-sm md:text-md text-red-500">
                  {`₵${formatPrice(product.price)}`}.00
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center mt-10">
          <Empty
            description={<span><strong>No Products Found</strong></span>}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            imageStyle={{ height: 200, marginBottom: 6 }}
          />
        </div>
      )}
    </div>
  );
};

export default Brand;
